import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import SessionDetailDrawer from 'components/session/SessionDetailDrawer';
import UserAvatar from 'components/shared/user/UserAvatar';
import { columnLabel, isVisibleColumn, renderCell } from 'components/shared/tables/responsive/cellRenderer';

// Columnas que suben a la CABECERA (identidad y estado) y por tanto no se repiten abajo.
const IDENTITY_COLUMNS = ['user', 'full_name', 'first_name', 'last_name', 'photo', 'name'];
const STATUS_COLUMNS = ['membership_status', 'status', 'state'];

/**
 * Panel lateral de DETALLE de una fila (modo card en mobile). Mismo lenguaje que "Detalle de
 * la sesión": entra deslizándose de derecha a izquierda, abre con la persona (foto, nombre y
 * chip de estado), sigue con las filas label → valor y cierra con las acciones al pie, en
 * botones de ancho completo (los de la tabla son de 34px y quedaban pegados en mobile).
 */
function TableDetailDrawer({
  open,
  onClose,
  columns,
  row = null,
  rowIndex = 0,
  allRows = [],
  actionsColumnName = 'actions'
}) {
  const { t } = useTranslation(['tables', 'labels']);

  const visible = columns.filter(isVisibleColumn);
  const actions = visible.find((c) => c.name === actionsColumnName) || null;
  const statusColumn = visible.find((c) => STATUS_COLUMNS.includes(c.name)) || null;
  const fields = visible.filter(
    (c) =>
      c.name !== actionsColumnName &&
      c.name !== statusColumn?.name &&
      !IDENTITY_COLUMNS.includes(c.name)
  );

  const person = row?.full_name ? { full_name: row.full_name, photo: row.photo } : null;
  const heading = person?.full_name || t('detail', { ns: 'tables', defaultValue: 'Detalle' });

  return (
    <SessionDetailDrawer open={open} onClose={onClose}>
      {/* El drawer se monta SIEMPRE (aunque `row` sea null) para que MUI pueda animar la
          entrada y la salida; sin esto el panel aparecía y desaparecía de golpe. */}
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
          {person && <UserAvatar border size={44} textSize={18} user={person} />}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} letterSpacing="-0.01em" noWrap>
              {heading}
            </Typography>
            {statusColumn && row && (
              <Box sx={{ mt: 0.5, '& > *': { maxWidth: '100%' } }}>
                {renderCell(statusColumn, row, rowIndex, columns.indexOf(statusColumn), allRows)}
              </Box>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t('close', { ns: 'tables', defaultValue: 'Cerrar' })}
            sx={{ flex: 'none', border: '1px solid', borderColor: 'hairline.main' }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <Stack spacing={0} sx={{ flex: 1, overflowY: 'auto', pt: 1 }}>
          {row &&
            fields.map((column, i) => (
              <Box key={column.name}>
                {i > 0 && <Divider sx={{ borderColor: 'hairline.main' }} />}
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ py: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      flex: 'none'
                    }}>
                    {columnLabel(column)}
                  </Typography>
                  <Box
                    sx={{
                      minWidth: 0,
                      textAlign: 'right',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      '& > *': { maxWidth: '100%' }
                    }}>
                    {renderCell(column, row, rowIndex, columns.indexOf(column), allRows)}
                  </Box>
                </Stack>
              </Box>
            ))}
        </Stack>

        {actions && row && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              pt: 2,
              mt: 1,
              borderTop: '1px solid',
              borderColor: 'hairline.main',
              backgroundColor: 'surface.sidebar'
            }}>
            {/* Las acciones vienen como iconos de tabla: aquí se estiran a ancho completo y
                al target táctil de 44px para que dejen de verse superpuestas en mobile. */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                '& > *': { display: 'flex', gap: 1, flex: 1, width: '100%' },
                '& .MuiIconButton-root, & .MuiButton-root': {
                  flex: 1,
                  minWidth: 44,
                  minHeight: 44,
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: 'hairline.main'
                }
              }}>
              {renderCell(actions, row, rowIndex, columns.indexOf(actions), allRows)}
            </Box>
          </Box>
        )}
      </Stack>
    </SessionDetailDrawer>
  );
}

TableDetailDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  row: PropTypes.object,
  rowIndex: PropTypes.number,
  allRows: PropTypes.array,
  actionsColumnName: PropTypes.string
};

export default TableDetailDrawer;
